/**
 * admin/quizzes/index.tsx — Admin quiz management (Phase F.1).
 *
 * Two tabs:
 *   - "Library" — approved library quizzes (source = 'library'). Filter
 *     by level (L1-L3, v0.1.4 — konsisten dengan halaman tutor).
 *     Each row links to the edit form.
 *   - "Submission Pending" — tutor-submitted quizzes awaiting admin
 *     review (source = 'library_pending'). Each row links to the
 *     review page (/admin/quiz-submissions/:id).
 *
 * "Buat Quiz Baru" CTA at the top right → /admin/quizzes/new.
 *
 * Data sources:
 *   - GET /api/quiz/library?sdGrade=N — approved library (tutor/admin only)
 *   - GET /api/quiz/admin/submissions — pending submissions (admin only)
 *
 * Both endpoints already exist from Phase E.2 wiring.
 */

import { type ReactNode, useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';
import {
  LEARNING_LEVELS,
  LEARNING_LEVEL_DOTS,
  LEARNING_LEVEL_LABEL,
  toLearningLevelEnum,
  type LearningLevel,
} from '../../../lib/leveling.js';

export const Route = createFileRoute('/admin/quizzes/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminQuizzesPage,
});

interface LibraryQuiz {
  id: string;
  title: string;
  description: string | null;
  source: 'library' | 'custom' | 'library_pending';
  sd_grade: number;
  is_active: number;
  updated_at: string;
  owner_user_id: string;
  reject_reason: string | null;
}

interface SubmissionRow {
  id: string;
  title: string;
  sd_grade: number;
  owner_name: string;
  question_count: number;
  updated_at: string;
}

type Tab = 'library' | 'submissions';

function levelBadge(sdGrade: number): string {
  const lv = toLearningLevelEnum(sdGrade);
  return lv ? `${lv} ${LEARNING_LEVEL_LABEL[lv]}` : '—';
}

function AdminQuizzesPage(): ReactNode {
  const [tab, setTab] = useState<Tab>('library');
  const [level, setLevel] = useState<LearningLevel | null>(null);

  // Library list (any role can call, but the page is admin-gated).
  // v0.1.4: fetch sekali tanpa sdGrade, filter level client-side.
  const libraryQuery = useQuery({
    queryKey: ['quiz', 'library'],
    queryFn: async () => {
      const r = await fetch('/api/quiz/library', { credentials: 'include' });
      const j = (await r.json()) as { ok: boolean; data?: { quizzes: LibraryQuiz[] }; error?: { message: string } };
      if (!j.ok || !j.data) throw new Error(j.error?.message ?? 'Gagal memuat library');
      return j.data.quizzes;
    },
  });

  const allLibrary = libraryQuery.data ?? [];
  const filteredLibrary = level
    ? allLibrary.filter((q) => toLearningLevelEnum(q.sd_grade) === level)
    : allLibrary;

  // Submissions list (admin only).
  const submissionsQuery = useQuery({
    queryKey: ['quiz', 'admin', 'submissions'],
    queryFn: async () => {
      const r = await fetch('/api/quiz/admin/submissions', { credentials: 'include' });
      const j = (await r.json()) as { ok: boolean; data?: SubmissionRow[]; error?: { message: string } };
      if (!j.ok || !j.data) throw new Error(j.error?.message ?? 'Gagal memuat submission');
      return j.data;
    },
    enabled: tab === 'submissions',
  });

  const pendingCount = submissionsQuery.data?.length ?? 0;

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Operasional"
        title="Kelola Quiz"
        description="Library quiz yang tampil untuk tutor + review submission kustom yang menunggu persetujuan."
        action={
          <Link to="/admin/quizzes/new" className="button button-primary" data-testid="quiz-new">
            + Buat Quiz Baru
          </Link>
        }
      />

      <div className="filter-cluster" role="tablist" aria-label="Tab kelola quiz">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'library'}
          className={`filter-segment${tab === 'library' ? ' is-active' : ''}`}
          onClick={() => setTab('library')}
          data-testid="tab-library"
        >
          Library
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'submissions'}
          className={`filter-segment${tab === 'submissions' ? ' is-active' : ''}`}
          onClick={() => setTab('submissions')}
          data-testid="tab-submissions"
        >
          <span>Submission Pending</span>
          {pendingCount > 0 ? (
            <span className="filter-segment-num" data-testid="pending-count">{pendingCount}</span>
          ) : null}
        </button>
      </div>

      {tab === 'library' ? (
        <section className="section" aria-labelledby="library-section">
          <div className="filter-cluster" role="group" aria-label="Level">
            <span className="filter-label">Level</span>
            <div className="filter-pills" data-testid="filter-level-group">
              {LEARNING_LEVELS.map((lv) => {
                const active = level === lv;
                const count = allLibrary.filter(
                  (q) => toLearningLevelEnum(q.sd_grade) === lv,
                ).length;
                return (
                  <button
                    key={lv}
                    type="button"
                    className={active ? 'filter-pill is-active' : 'filter-pill'}
                    aria-pressed={active}
                    onClick={() => setLevel(active ? null : lv)}
                    data-testid={`level-pill-${lv}`}
                  >
                    <span className="filter-pill-dots" aria-hidden="true">
                      {Array.from({ length: 3 }, (_, i) => (
                        <span
                          key={i}
                          className={
                            i < LEARNING_LEVEL_DOTS[lv]
                              ? 'filter-pill-dot is-on'
                              : 'filter-pill-dot'
                          }
                        />
                      ))}
                    </span>
                    <span className="filter-pill-label">
                      {lv} {LEARNING_LEVEL_LABEL[lv]}
                    </span>
                    <span className="filter-pill-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {libraryQuery.isLoading ? (
            <StatePanel variant="empty" title="Memuat library…" />
          ) : libraryQuery.isError ? (
            <StatePanel
              variant="error"
              title="Gagal memuat library"
              description={(libraryQuery.error as Error).message}
            />
          ) : filteredLibrary.length === 0 ? (
            <StatePanel
              variant="empty"
              title="Library kosong"
              description={level ? `Tidak ada quiz library untuk level ${level} ${LEARNING_LEVEL_LABEL[level]}.` : 'Belum ada quiz library. Buat quiz baru untuk memulai.'}
              action={
                <Link to="/admin/quizzes/new" className="button button-primary">
                  Buat Quiz Pertama
                </Link>
              }
            />
          ) : (
            <ul className="quiz-list" data-testid="library-list">
              {filteredLibrary.map((q) => (
                <li key={q.id} className="card quiz-list-item">
                  <div className="quiz-list-meta">
                    <span className="card-meta">{levelBadge(q.sd_grade)}</span>
                  </div>
                  <h3 className="card-title">
                    <Link
                      to="/admin/quizzes/$id"
                      params={{ id: q.id }}
                      data-testid="library-item"
                    >
                      {q.title}
                    </Link>
                  </h3>
                  {q.description ? <p className="quiz-list-desc">{q.description}</p> : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <section className="section" aria-labelledby="submissions-section">
          {submissionsQuery.isLoading ? (
            <StatePanel variant="empty" title="Memuat submission…" />
          ) : submissionsQuery.isError ? (
            <StatePanel
              variant="error"
              title="Gagal memuat submission"
              description={(submissionsQuery.error as Error).message}
            />
          ) : !submissionsQuery.data || submissionsQuery.data.length === 0 ? (
            <StatePanel
              variant="empty"
              title="Tidak ada submission"
              description="Belum ada kustom quiz yang menunggu review."
            />
          ) : (
            <ul className="quiz-list" data-testid="submissions-list">
              {submissionsQuery.data.map((s) => (
                <li key={s.id} className="card quiz-list-item">
                  <div className="quiz-list-meta">
                    <span className="card-meta">{levelBadge(s.sd_grade)}</span>
                    <span className="card-meta">{s.question_count} soal</span>
                  </div>
                  <h3 className="card-title">
                    <Link
                      to="/admin/quiz-submissions/$id"
                      params={{ id: s.id }}
                      data-testid="submission-item"
                    >
                      {s.title}
                    </Link>
                  </h3>
                  <p className="quiz-list-desc">Dari: {s.owner_name}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </main>
  );
}
